import { createContext, useContext, useState, type ReactNode } from "react";

export type Severity = "Critical" | "High" | "Medium" | "Low";
export type AlertStatus = "Open" | "Acknowledged";

export interface Alert {
  id: string;
  title: string;
  source: string;
  severity: Severity;
  status: AlertStatus;
  assignee: string | null;
  ts: string;
}

export const INITIAL_ALERTS: Alert[] = [];

interface AlertsContextValue {
  alerts: Alert[];
  acknowledge: (id: string) => void;
  openCount: number;
  criticalOpenCount: number;
}

const AlertsContext = createContext<AlertsContextValue | null>(null);

export function AlertsProvider({ children }: { children: ReactNode }): JSX.Element {
  const [alerts, setAlerts] = useState<Alert[]>(INITIAL_ALERTS);

  function acknowledge(id: string): void {
    setAlerts(prev =>
      prev.map(a => (a.id === id ? { ...a, status: "Acknowledged" as AlertStatus } : a))
    );
  }

  const openCount        = alerts.filter(a => a.status === "Open").length;
  const criticalOpenCount = alerts.filter(a => a.severity === "Critical" && a.status === "Open").length;

  return (
    <AlertsContext.Provider value={{ alerts, acknowledge, openCount, criticalOpenCount }}>
      {children}
    </AlertsContext.Provider>
  );
}

export function useAlerts(): AlertsContextValue {
  const ctx = useContext(AlertsContext);
  if (!ctx) throw new Error("useAlerts must be used within AlertsProvider");
  return ctx;
}
